import {pagesPath, AccountRouteName} from '@/ts/router/pages-path';
import type {RouteRecordRaw} from 'vue-router';
import MainBasePage from '@/vue/pages/main-base-page.vue';
import {interventionPages} from '@/ts/router/pages/intervention-pages';
import InterventionsListPage from '@/vue/pages/interventions/interventions-list-page.vue';
import InterventionFetchPage from '@/vue/pages/interventions/intervention-fetch-page.vue';
import {sessionPages} from '@/ts/router/pages/session-pages';
import AccountPage from '@/vue/pages/user/account-page.vue';
import AccountProfileTabPage from '@/vue/pages/user/account-profile-tab-page.vue';
import AccountSubscriptionTabPage from '@/vue/pages/user/account-subscription-tab-page.vue';
import AccountSecurityTabPage from '@/vue/pages/user/account-security-tab-page.vue';
import AccountBadgesTabPage from '@/vue/pages/user/account-badges-tab-page.vue';
import AccountPreferencesTabPage from '@/vue/pages/user/account-preferences-tab-page.vue';
import AccountDebugTabPage from '@/vue/pages/user/account-debug-tab-page.vue';
import InterventionLandingPage from '@/vue/pages/interventions/intervention-landing-page.vue';
import ExplorePage from '@/vue/pages/explore-page.vue';
import RecommendationPage from '@/vue/pages/recco/recommendation-page.vue';
import TopicPage from '@/vue/pages/recco/topic-page.vue';
import PartnerContentPage from '@/vue/pages/recco/partner-content-page.vue';
import ChallengePage from '@/vue/pages/challenge-page.vue';
import ChallengeSessionPage from '@/vue/pages/challenge-session-page.vue';
import ConsentRequiredPage from '@/vue/pages/consent-required-page.vue';
import JournalPage from '@/vue/pages/user/journal-page.vue';
import TodayPage from '@/vue/pages/user/today-page.vue';
import BlockContentPage from '@/vue/pages/block-content/block-content-page.vue';

/** Root route definition for the authenticated main app, containing all primary user-facing pages. */
export const mainPages: RouteRecordRaw = {
  path: pagesPath.main.home,
  component: MainBasePage,
  meta: {
    auth: true,
    medicalDevice: false,
  },
  children: [
    {
      path: '',
      component: TodayPage,
      meta: {
        medicalDevice: false,
      },
    },
    {
      path: pagesPath.main.explore,
      component: ExplorePage,
      meta: {
        medicalDevice: false,
      },
    },
    // Legacy route: the page used to live at /prevent — redirect old links to /explore,
    // preserving any query/hash on the incoming URL.
    {
      path: '/prevent',
      redirect: (to) => ({path: pagesPath.main.explore, query: to.query, hash: to.hash}),
    },
    {
      path: pagesPath.main.journal,
      component: JournalPage,
      meta: {
        medicalDevice: false,
      },
    },
    // The redesigned Today page is the home page now (IPD-3077), so it hangs off the root the way
    // the page it replaces did, and everything that already pointed there — the logo, the landing
    // after sign-in, the button on the error screens — arrives at it without knowing.
    //
    // `/today` is where it lived while it was being built, and the team has been typing that for
    // weeks. Kept as a redirect rather than dropped, the same way `/prevent` above is, so nobody's
    // bookmark breaks and the root stays the one address the app calls home.
    //
    // The language comes off the incoming address rather than from the bare root. Sent to `'/'` the
    // router resolves the language again from the stored preference, so a reader whose preference
    // is English opening a German link would land in English — the redirect resolves before the
    // guard that would otherwise reconcile the two. `/prevent` above has that shape and this one
    // deliberately does not copy it.
    {
      path: pagesPath.main.today,
      // `:lang` is required on every record the language prefix clones, and the bare root is a
      // separate redirect that never reaches this one, so the param is always there to read.
      redirect: (to) => ({
        path: `/${String(to.params.lang)}`,
        query: to.query,
        hash: to.hash,
      }),
    },
    {
      path: pagesPath.content.recommendation,
      component: RecommendationPage,
      name: pagesPath.content.recommendation,
      meta: {
        medicalDevice: false,
      },
    },
    {
      path: 'block-content/:itemId',
      component: BlockContentPage,
      name: 'block-content',
      meta: {
        medicalDevice: false,
      },
    },
    {
      path: pagesPath.content.challenge,
      component: ChallengePage,
      name: pagesPath.content.challenge,
      meta: {
        medicalDevice: false,
      },
    },
    {
      path: pagesPath.content.challengeSession,
      component: ChallengeSessionPage,
      name: pagesPath.content.challengeSession,
      meta: {
        medicalDevice: false,
      },
    },
    {
      path: pagesPath.topic.topic,
      component: TopicPage,
      name: 'topic',
      meta: {
        medicalDevice: false,
      },
    },
    {
      path: pagesPath.topic.partnerContent,
      component: PartnerContentPage,
      name: 'partner-content',
      meta: {
        medicalDevice: false,
      },
    },
    // Legacy route redirects to Account > Security tab
    {
      path: pagesPath.account.changePassword,
      redirect: pagesPath.account.security,
    },
    {
      path: pagesPath.account.account,
      component: AccountPage,
      redirect: pagesPath.account.profile,
      // The whole account area stays reachable when the contract is expired so
      // the user can renew, change password, or log out (IPD-2661). Exemption is
      // declared here (not pattern-matched in the guard) so it travels with the
      // route and is discoverable by anyone adding/moving account pages.
      meta: {
        allowExpired: true,
        medicalDevice: false,
      },
      children: [
        {
          path: 'profile',
          name: AccountRouteName.profile,
          component: AccountProfileTabPage,
          meta: {
            // Deleting the account stays possible without consenting: the backend exempts `/me`
            // (its update and delete included) from the terms and privacy filters (IPD-3536).
            allowPendingConsent: true,
            medicalDevice: false,
          },
        },
        {
          path: 'subscription',
          name: AccountRouteName.subscription,
          component: AccountSubscriptionTabPage,
          meta: {
            medicalDevice: false,
          },
        },
        {
          path: 'security',
          name: AccountRouteName.security,
          component: AccountSecurityTabPage,
          meta: {
            medicalDevice: false,
          },
        },
        {
          path: 'preferences',
          name: AccountRouteName.preferences,
          component: AccountPreferencesTabPage,
          meta: {
            medicalDevice: false,
          },
        },
        {
          path: 'badges',
          name: AccountRouteName.badges,
          component: AccountBadgesTabPage,
          meta: {
            medicalDevice: false,
          },
        },
        {
          path: 'debug',
          name: AccountRouteName.debug,
          component: AccountDebugTabPage,
          meta: {
            medicalDevice: false,
          },
        },
      ],
    },
    {
      path: pagesPath.intervention.interventionsList,
      component: InterventionsListPage,
      meta: {
        medicalDevice: true,
      },
      children: [
        {
          path: pagesPath.intervention.interventionFetch,
          components: {
            default: InterventionsListPage,
            modal: InterventionFetchPage,
          },
          meta: {
            medicalDevice: true,
          },
          children: [
            ...interventionPages,
          ],
        },
      ],
    },
    {
      path: pagesPath.intervention.interventionLanding,
      component: InterventionLandingPage,
      meta: {
        medicalDevice: true,
      },
    },
    ...sessionPages,
  ],
};

/** Route definition for the consent-required page shown when mandatory user consent is pending. */
export const consentRequiredRoute: RouteRecordRaw = {
  path: pagesPath.main.consentRequired,
  component: ConsentRequiredPage,
  meta: {
    auth: true,
    // Mandatory-consent gating is orthogonal to (and takes precedence over)
    // contract expiry — never bounce a user away from the consent flow.
    allowExpired: true,
    consentFlow: true,
    medicalDevice: true,
  },
};
